import React from 'react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  HALE_TEXT_FAINT, HALE_TEXT_MUTED, HALE_HAIR, HALE_INK, HALE_RED,
  HALE_DISPLAY, HALE_MONO,
} from './_design';

/* Shared field primitives for the HALE intake. Modern marketplace system —
 * white surface, Chivo display headings, bordered rounded inputs, Hale red
 * accent — matching the Hale landing page and brand console. */

export function StepHeader({ kicker, title, sub }) {
  return (
    <div className="mb-9">
      <p className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: HALE_RED, fontFamily: HALE_MONO }}>{kicker}</p>
      <h2 className="text-2xl sm:text-3xl font-black mt-3 leading-[1.1] tracking-[-0.02em]"
          style={{ fontFamily: HALE_DISPLAY, color: HALE_INK }}>
        {title}
      </h2>
      {sub && <p className="text-[14px] mt-3 max-w-xl leading-relaxed" style={{ color: HALE_TEXT_MUTED }}>{sub}</p>}
    </div>
  );
}

export function Field({ label, hint, optional, children, full }) {
  return (
    <label className={`block ${full ? 'col-span-2' : ''}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[12px] font-semibold" style={{ color: HALE_INK }}>{label}</span>
        {optional && <span className="text-[9px] uppercase tracking-[0.18em]"
                            style={{ color: HALE_TEXT_FAINT, fontFamily: HALE_MONO }}>optional</span>}
      </div>
      {children}
      {hint && <p className="text-[11.5px] mt-1.5 leading-relaxed" style={{ color: HALE_TEXT_FAINT }}>{hint}</p>}
    </label>
  );
}

export function HaleInput(props) {
  return (
    <Input
      {...props}
      className={
        'h-11 rounded-lg text-[14px] px-3.5 ' +
        'placeholder:text-[#A1A1AA] focus-visible:ring-0 focus-visible:border-[#E63946] ' +
        (props.className || '')
      }
      style={{ borderColor: HALE_HAIR, backgroundColor: '#FFFFFF', color: HALE_INK, ...(props.style || {}) }}
    />
  );
}

export function HaleTextarea(props) {
  return (
    <Textarea
      {...props}
      className={
        'rounded-lg text-[14px] px-3.5 py-2.5 placeholder:text-[#A1A1AA] ' +
        'focus-visible:ring-0 focus-visible:border-[#E63946] ' +
        (props.className || '')
      }
      style={{ borderColor: HALE_HAIR, backgroundColor: '#FFFFFF', color: HALE_INK, ...(props.style || {}) }}
    />
  );
}

export function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={
        'px-4 py-2 text-[12px] font-semibold rounded-full border transition-all ' +
        (active
          ? 'bg-[#E63946] text-white border-[#E63946]'
          : 'bg-white border-[#E4E4E7] hover:border-[#111214] text-[#71717A] hover:text-[#111214]')
      }
    >
      {children}
    </button>
  );
}

export function ChipGrid({ options, value = [], onToggle, testidPrefix }) {
  const arr = Array.isArray(value) ? value : [];
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const k = typeof opt === 'string' ? opt : opt.key;
        const label = typeof opt === 'string' ? opt : opt.label;
        return (
          <Chip key={k} active={arr.includes(k)} onClick={() => onToggle(k)}
                testid={testidPrefix ? `${testidPrefix}-${k}` : undefined}>
            {label}
          </Chip>
        );
      })}
    </div>
  );
}

export function Stepper({ current, total }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => {
        const idx = i + 1;
        const isDone = idx < current;
        const isOn = idx === current;
        return (
          <div key={idx} className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold tabular-nums transition-all"
              style={{
                backgroundColor: isOn ? HALE_RED : (isDone ? 'rgba(230,57,70,0.12)' : '#FFFFFF'),
                color: isOn ? '#FFFFFF' : (isDone ? HALE_RED : HALE_TEXT_FAINT),
                border: isOn ? `1px solid ${HALE_RED}` : `1px solid ${HALE_HAIR}`,
                fontFamily: HALE_DISPLAY,
              }}
            >
              {idx}
            </div>
            {idx < total && <div className="w-7 h-px" style={{ backgroundColor: HALE_HAIR }} />}
          </div>
        );
      })}
    </div>
  );
}
